/**
 * Google Analytics 4 loader — consent-gated, production-domain-gated.
 *
 * Mirrors the Yandex.Metrika loader's shape (see sibling file) so the
 * analytics provider can install both trackers with the same watcher
 * pattern. This module owns only the mechanics of injecting gtag.js and
 * wiring the dataLayer/gtag stub; the decision of whether to fire lives
 * in the analytics provider.
 *
 * Two guards run before the network request:
 *   1. Hostname whitelist — only the production host loads the tag.
 *      Dev, vite preview, staging, and preview deploys are silenced so
 *      the GA4 property never records non-production sessions even if a
 *      developer accepts cookies locally.
 *   2. In-memory load flag — idempotent; a second call after successful
 *      load is a no-op. Prevents double-init during rapid consent toggles.
 *
 * On revoke we call GA4's consent-mode update (`analytics_storage: denied`)
 * instead of trying to unload gtag.js — unloading a running tag is not
 * supported by the library. True revocation takes effect at the next page
 * load, where the consent store will already report `analytics: false`
 * and this loader will never run.
 */

const GA_MEASUREMENT_ID = 'G-62J2BT08B6'

const PRODUCTION_HOSTS = new Set([
  'marketassist.ru',
  'www.marketassist.ru',
])

// Global stubs on window for the gtag.js runtime. Shape matches the
// vendor-recommended snippet — `dataLayer` is the event queue, `gtag`
// is the thin wrapper that pushes onto it.
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let loaded = false

/**
 * Returns true when this page is on a production host. Exposed so the
 * analytics provider can skip consent wiring entirely in non-production
 * environments, matching the Yandex helper's contract.
 */
export function isGoogleAnalyticsLoadable(): boolean {
  if (typeof window === 'undefined')
    return false
  return PRODUCTION_HOSTS.has(window.location.hostname)
}

/**
 * Inject gtag.js and fire the measurement id's config. Idempotent. Safe
 * to call on non-production hosts — returns without touching the DOM
 * or the network.
 */
export function loadGoogleAnalytics(): void {
  if (loaded)
    return
  if (!isGoogleAnalyticsLoadable())
    return

  // Set early so a second watcher firing before the script lands does
  // not queue up another injection.
  loaded = true

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`

  // Install the dataLayer + gtag stub up-front so any gtag(...) calls
  // made before the script lands are captured on the queue and replayed
  // once the real library takes over. Matches the official snippet.
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function (...args: unknown[]): void {
      window.dataLayer!.push(args)
    }
  }

  // Guard against double injection if something else already added
  // gtag.js with this measurement id.
  const alreadyInjected = Array.from(document.scripts).some(
    s => s.src === scriptSrc,
  )
  if (!alreadyInjected) {
    const script = document.createElement('script')
    const firstScript = document.getElementsByTagName('script')[0]
    script.async = true
    script.src = scriptSrc
    firstScript?.parentNode?.insertBefore(script, firstScript)
  }

  // We only get here because the user granted the `analytics` category,
  // so analytics_storage is 'granted'. Advertising signals require the
  // separate `marketing` category — keep them off here and let a future
  // refinement wire them up if/when needed.
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  })

  window.gtag('js', new Date())
  window.gtag('config', GA_MEASUREMENT_ID, {
    // GA4 anonymises by default, but be explicit — we want the setting
    // to survive any future GA4 behaviour changes.
    anonymize_ip: true,
    // Keep ad-personalisation signals off unless the user has opted in
    // via a future marketing-consent wiring.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  })
}

/**
 * Halt GA4 for the remainder of this page via consent-mode update, and
 * mark the loader as "done" so future flips back to granted inside the
 * same session do not re-init the tag on top of itself. True revocation
 * takes effect on the next page load, where consent will already be
 * `analytics: false` and this module's `load*` will never run.
 */
export function resetGoogleAnalytics(): void {
  // Keep the loaded flag set so a flip back to granted does not cause
  // a second init on a tag that may already be running.
  loaded = true

  if (typeof window === 'undefined' || !window.gtag)
    return

  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  })
}
